import { useState, useEffect, useRef, useCallback } from "react";

interface UseInfiniteScrollProps<T> {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  fetchedData: T[] | null | undefined;
  pagination: { total: number; limit: number } | null | undefined;
  isPending: boolean;
  resetDependencies: any[];
  isDuplicate: (oldItem: T, newItem: T) => boolean;
}

export const useInfiniteScroll = <T>({
  page,
  setPage,
  fetchedData,
  pagination,
  isPending,
  resetDependencies,
  isDuplicate,
}: UseInfiniteScrollProps<T>) => {
  const [accumulatedData, setAccumulatedData] = useState<T[]>([]);
  const scrollTimeout = useRef<number | null>(null);

  // Keep the latest function ref to prevent stale closures
  const isDuplicateRef = useRef(isDuplicate);
  useEffect(() => {
    isDuplicateRef.current = isDuplicate;
  }, [isDuplicate]);

  // Reset completely when parent IDs or objects change
  useEffect(() => {
    setPage(1);
    setAccumulatedData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDependencies);

  // Accumulate new page data and filter out duplicates
  useEffect(() => {
    if (fetchedData && Array.isArray(fetchedData)) {
      setAccumulatedData((prev) => {
        if (page === 1) return fetchedData;

        const newItems = fetchedData.filter(
          (newItem) => !prev.some((oldItem) => isDuplicateRef.current(oldItem, newItem))
        );
        return newItems.length === 0 ? prev : [...prev, ...newItems];
      });
    }
  }, [fetchedData, page]);

  // Handle user scrolling to the bottom of the div container
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (scrollTimeout.current) window.clearTimeout(scrollTimeout.current);

    scrollTimeout.current = window.setTimeout(() => {
      if (!target) return;
      const threshold = 50;
      const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

      if (atBottom && pagination && page * pagination.limit < pagination.total && !isPending) {
        setPage((prevPage) => prevPage + 1);
      }
    }, 100);
  }, [pagination, page, isPending, setPage]);

  const resetData = useCallback(() => {
    setPage(1);
    setAccumulatedData([]);
  }, [setPage]);

  return { accumulatedData, handleScroll, resetData };
};